
import { supabase } from '@/lib/supabase';

export interface DealerCredit {
  id: string;
  dealer_id: string;
  credits_balance: number;
  created_at: string;
  updated_at: string;
}

export interface DealerCreditTransaction {
  id: string;
  dealer_id: string;
  transaction_type: 'DEBIT' | 'CREDIT' | 'PURCHASE' | 'INITIAL' | 'MIGRATION';
  amount: number;
  description: string;
  reference_id?: string;
  created_at: string;
}

export interface DealerCreditPackage {
  id: string;
  name: string;
  credits: number;
  price_euros: number;
  bonus_credits: number;
  active: boolean;
}

export class DealerCreditService {
  /**
   * Verifica il saldo crediti di un dealer
   */
  static async checkDealerCredits(dealerId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('dealer_credits')
        .select('credits_balance')
        .eq('dealer_id', dealerId)
        .single();

      if (error) {
        console.error('Error checking dealer credits:', error);
        return 0;
      }

      return data?.credits_balance || 0;
    } catch (error) {
      console.error('Exception checking dealer credits:', error);
      return 0;
    }
  }

  /**
   * Sottrae 1 credito dal dealer (per price lock)
   */
  static async deductCredit(dealerId: string, priceLockId?: string): Promise<boolean> {
    try {
      // Prima verifica se ha crediti sufficienti
      const currentCredits = await this.checkDealerCredits(dealerId);
      if (currentCredits < 1) {
        console.warn(`Dealer ${dealerId} has insufficient credits: ${currentCredits}`);
        return false;
      }

      // Sottrai 1 credito in una transazione atomica
      const { error: updateError } = await supabase
        .from('dealer_credits')
        .update({ 
          credits_balance: currentCredits - 1,
          updated_at: new Date().toISOString()
        })
        .eq('dealer_id', dealerId);

      if (updateError) {
        console.error('Error updating dealer credits:', updateError);
        return false;
      }

      // Registra la transazione
      const { error: transactionError } = await supabase
        .from('dealer_credit_transactions')
        .insert({
          dealer_id: dealerId,
          transaction_type: 'DEBIT',
          amount: -1,
          description: 'Credito utilizzato per blocco prezzo',
          reference_id: priceLockId
        });

      if (transactionError) {
        console.error('Error logging credit transaction:', transactionError);
        // Non fallire se il logging non funziona
      }

      console.log(`Successfully deducted 1 credit from dealer ${dealerId}. New balance: ${currentCredits - 1}`);
      return true;
    } catch (error) {
      console.error('Exception deducting credit:', error);
      return false;
    }
  }

  /**
   * Aggiunge crediti al dealer
   */
  static async addCredits(
    dealerId: string, 
    amount: number, 
    type: 'CREDIT' | 'PURCHASE' | 'INITIAL' | 'MIGRATION',
    description: string,
    referenceId?: string
  ): Promise<boolean> {
    try {
      const currentCredits = await this.checkDealerCredits(dealerId);
      const newBalance = currentCredits + amount;

      // Aggiorna il saldo
      const { error: updateError } = await supabase
        .from('dealer_credits')
        .update({ 
          credits_balance: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('dealer_id', dealerId);

      if (updateError) {
        console.error('Error adding dealer credits:', updateError);
        return false;
      }

      // Registra la transazione
      const { error: transactionError } = await supabase
        .from('dealer_credit_transactions')
        .insert({
          dealer_id: dealerId,
          transaction_type: type,
          amount: amount,
          description: description,
          reference_id: referenceId
        });

      if (transactionError) {
        console.error('Error logging credit addition:', transactionError);
      }

      console.log(`Successfully added ${amount} credits to dealer ${dealerId}. New balance: ${newBalance}`);
      return true;
    } catch (error) {
      console.error('Exception adding credits:', error);
      return false;
    }
  }

  /**
   * Ottiene lo storico delle transazioni crediti
   */
  static async getCreditHistory(dealerId: string): Promise<DealerCreditTransaction[]> {
    try {
      const { data, error } = await supabase
        .from('dealer_credit_transactions')
        .select('*')
        .eq('dealer_id', dealerId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching credit history:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Exception fetching credit history:', error);
      return [];
    }
  }

  /**
   * Ottiene i pacchetti crediti disponibili
   */
  static async getCreditPackages(): Promise<DealerCreditPackage[]> {
    try {
      const { data, error } = await supabase
        .from('dealer_credit_packages')
        .select('*')
        .eq('active', true)
        .order('price_euros', { ascending: true });

      if (error) {
        console.error('Error fetching credit packages:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Exception fetching credit packages:', error);
      return [];
    }
  }

  /**
   * Verifica se un dealer ha crediti sufficienti per il price lock
   */
  static async canUsePriceLock(dealerId: string): Promise<boolean> {
    const credits = await this.checkDealerCredits(dealerId);
    return credits >= 1;
  }
}
